/**
 * Workflow Upload Sidebar
 *
 * Simplified workflow upload in a right sidebar.
 * Auto-fills workflow name from filename.
 */

import React, { useState } from 'react';


interface WorkflowUploadSidebarProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const WorkflowUploadSidebar: React.FC<WorkflowUploadSidebarProps> = ({
  tenantId,
  apiBaseUrl,
  authToken,
  onClose,
  onSuccess
}) => {
  const [comfyWorkflowFile, setComfyWorkflowFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{ errors?: string[]; warnings?: string[] } | null>(null);

  // Auto-populate display name from filename when file is selected
  const handleFileChange = (file: File | null) => {
    setComfyWorkflowFile(file);
    if (file && !displayName) {
      // Convert filename to display name: "furniture_MaterialSwap_API.json" → "Furniture MaterialSwap API"
      const nameWithoutExt = file.name.replace(/\.json$/i, '');
      const formatted = nameWithoutExt
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setDisplayName(formatted);
    }
  };

  const handleUpload = async () => {
    setError(null);
    setErrorDetails(null);

    if (!comfyWorkflowFile) {
      setError('Please select a workflow file');
      return;
    }

    if (!displayName.trim()) {
      setError('Please enter a workflow name');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('comfyWorkflow', comfyWorkflowFile);
      formData.append('displayName', displayName);

      let response: Response;
      try {
        response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/workflows`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formData
        });
      } catch (networkErr: any) {
        throw new Error(
          'Could not reach the server. Make sure Azure Functions is running on ' +
          `${apiBaseUrl} and try again. (${networkErr.message})`
        );
      }

      if (!response.ok) {
        // Try to parse response as JSON; fall back gracefully for HTML error pages
        let data: any = {};
        const responseText = await response.text();
        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error(`Upload failed (HTTP ${response.status}): ${response.statusText || 'Unknown error'}`);
        }

        // Capture compilation error details for structured display
        const hasDetails = data.details?.errors?.length > 0 || data.details?.warnings?.length > 0;
        if (hasDetails) {
          setErrorDetails(data.details);
        }

        // When specific errors are listed below, keep the title short.
        // When there are no details, append the message for context.
        const title = data.error || 'Upload failed';
        const body = (!hasDetails && data.message) ? `: ${data.message}` : '';
        throw new Error(title + body);
      }

      const result = await response.json();
      const isUpdate = result.isUpdate || false;
      const message = isUpdate
        ? `Workflow updated to version ${result.metadata.version}!`
        : 'Workflow uploaded successfully!';

      alert(message);
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400 disabled:bg-gray-100';
  const priBtn = 'flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50';
  const secBtn = 'px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="w-96 flex-shrink-0 bg-white shadow-xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upload Workflow</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="workflow-file-input" className="text-label font-medium text-gray-700">
              Select ComfyUI Workflow (JSON)
            </label>
            <input id="workflow-file-input" type="file" accept=".json"
              onChange={e => handleFileChange(e.target.files?.[0] || null)}
              className="text-label text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-label file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" />
            {comfyWorkflowFile && <span className="text-meta text-success-dark">✓ {comfyWorkflowFile.name}</span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="workflow-name-input" className="text-label font-medium text-gray-700">Workflow Name</label>
            <input id="workflow-name-input" type="text" value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g., Furniture Material Swap" className={inputCls} />
            <p className="text-meta text-gray-400">Auto-filled from filename. Edit if needed.</p>
          </div>

          {error && (
            <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">
              <strong>Error:</strong> {error}
              {errorDetails?.errors && errorDetails.errors.length > 0 && (
                <ul className="list-disc list-inside mt-2 flex flex-col gap-1">
                  {errorDetails.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {errorDetails?.warnings && errorDetails.warnings.length > 0 && (
                <div className="mt-2">
                  <strong>Warnings:</strong>
                  <ul className="list-disc list-inside mt-1 flex flex-col gap-1">
                    {errorDetails.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="bg-info-light border border-info/30 rounded-lg p-4 text-label text-info-dark">
            <strong>Note:</strong> If a workflow with this name already exists, it will be updated with a new version number.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button type="button" onClick={handleUpload} disabled={uploading || !comfyWorkflowFile || !displayName.trim()}
            className={priBtn}>{uploading ? 'Uploading…' : 'Upload'}</button>
          <button type="button" onClick={onClose} disabled={uploading} className={secBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

